// @flow

import React, { Component, Fragment } from "react";
import { noop, isEqual, isString, get, round } from "lodash";
import formatCurrency from "@tvg/formatter/currency";
import formatSequencial from "@tvg/formatter/numeric";
import {
  getCurrentProbableValue,
  doesRaceWagerTypeHasProbables
} from "@fdr/utils/potentialReturnUtils";
import { type NullaryFn, type UnaryFn } from "@tvg/types/Functional";
import type { RaceTypeCodeEnum, Probable, RaceStatus } from "@tvg/types/Race";
import buildColor from "@fdr/static-ui/ColorPalette";
import Icon from "@fdr/static-ui/Icons";
import { info, trash } from "@fdr/static-ui/Icons/icons";
import { WagerGroupNames } from "@fdr/types/WagerTypes";
import { Loading } from "@fdr/static-ui/ProcessingOverlay/styled-components";
import type { WagerType } from "@tvg/types/Wager";
import RunnerNumberGroup from "../RunnerNumberGroup";
import Button from "../../_atoms/Button";
import Tag from "../../_atoms/Tag";

import {
  BetDetailsContainer,
  BetDetails,
  SelectionsContainer,
  BetSummaryContainer,
  BetType,
  TicketAmount,
  BetLabel,
  BetInfoTitle,
  BetInfoSubTitle,
  BetLabelWrapper,
  BetValue,
  ProbablesAmount,
  TrashContainer,
  RedCircle,
  TagContainer
} from "./styled-components";
import { IconExclamationContainer } from "../../_templates/BetTicket/styled-components";

type Props = {
  /**
   * Callback to be called to cancel the bet
   */
  onDelete: NullaryFn<mixed>,
  /**
   * If bet can be canceled (true) or not (false)
   */
  isCancelable: boolean,
  /**
   * Total value of the bet
   */
  betTotal: number,
  /**
   * Wager Amount of the bet
   */
  wagerAmount: number,
  /**
   * Code of the wager type
   */
  selectionType: string,
  /**
   * Array of selections [{runners, selectionType}]
   */
  selections: Array<Array<string>>,
  /**
   * Array of favorite runners
   */
  favoriteRunners: Array<Array<string>>,
  /**
   * Race Type Code
   */
  raceTypeCode: RaceTypeCodeEnum,
  /**
   * Is Key flag
   */
  isKey: boolean,
  /**
   * Is Leg flag
   */
  isLeg: boolean,
  /**
   * display vertically runners selections
   */
  isVertical: boolean,
  /**
   * Whenever it should display a lost tag
   */
  isLost: boolean,
  /**
   * Whenever it should display a cancelled tag
   */
  isCancelled: boolean,
  /**
   * Whenever it should display a refunded tag
   */
  isRefunded: boolean,
  /**
   * When given, this value will show on a green tag
   */
  totalWinAmount: number | null,
  /**
   * The race number corresponding to this bet summary
   */
  raceNumber: string | number,
  /**
   * The bet sumary layout
   */
  layout: "full" | "simple",
  /**
   * className to extend the default style
   */
  className: string,
  /**
   * boolean to determine wether to show full pick selection or just part of it.
   */
  showAllSelections: boolean,
  /**
   * border width
   */
  borderTotalWidth: boolean,
  /**
   * Displays border on top or bottom
   */
  isBorderBottom: boolean,
  /**
   * Probables
   */
  probables: Probable[],
  /**
   * wager type
   */
  wagerType: WagerType,
  /**
   *  Toggles potential return feature
   */
  showPotentialReturn: boolean,
  /**
   * Show modal potential return
   */
  openPotencialReturnModal: UnaryFn<boolean, void>,
  /*
   * Show processing overlay
   */
  isProcessing: boolean,
  /**
   * Array of Horse Names
   */
  horseNamesList: Array<string>,
  isBetPlaced: boolean,
  isBettingError: boolean,
  showTag: boolean,
  remainingDayCancelledCount: number,
  status: RaceStatus
};

export const getNumberOfWagers = (
  betTotal: number,
  wagerAmount: number
): number => round(betTotal / wagerAmount);

export const getCanShowFullLabel = (selectionsList: string[][]) => {
  const win = get(selectionsList, 0, []).length;
  const place = get(selectionsList, 1, []).length;
  const show = get(selectionsList, 2, []).length;
  let canShowFullLabel = true;

  if (win && place && show) {
    canShowFullLabel = win + place + show <= 7;
  } else if (!win && place && show) {
    canShowFullLabel = place + show <= 9;
  } else if ((win && place) || (win && show)) {
    canShowFullLabel = win + place + show <= 10;
  }

  return canShowFullLabel;
};

export const getListRunnerNumberGroup = (
  horseNamesList: string[] = [],
  selectionsList: string[][] = [],
  raceTypeCode: RaceTypeCodeEnum = "T",
  isKey: boolean = false,
  isLeg: boolean = false,
  raceNumber: number = 1,
  favoriteRunners: string[][] = [],
  betLeg: number = 0,
  showAllSelections: boolean = true,
  isVertical: boolean = false,
  isSpecialGroup: boolean = false,
  hasScrollOverflow: boolean = false,
  useNewProgramPageLayout: boolean = false
) => {
  const multipleColumns = selectionsList.length > 1;
  let showFullLabel = true;
  if (isSpecialGroup) {
    showFullLabel = getCanShowFullLabel(selectionsList);
  }

  return (
    <Fragment>
      {(!showAllSelections && selectionsList.length > 5
        ? selectionsList.slice(0, 5)
        : selectionsList
      ).map((selection, index) => {
        if (isKey && index > 1) {
          return false;
        }
        const favoritesColumn = isLeg ? index : 0;
        const favorites = favoriteRunners[favoritesColumn] || [];
        const favoritesAsStrings = favorites.every((fav) => isString(fav));
        const normalizedFavs = favoritesAsStrings
          ? favorites
          : favorites.map((fav) => `${fav}`);

        let key = false;
        let label = "";

        if (multipleColumns) {
          key = index === 0 && isKey;
          if (isKey && index !== 0) {
            label = "W/";
          } else if (isSpecialGroup) {
            label = showFullLabel
              ? get(WagerGroupNames, `winGroup.names.${index}`, "")
              : get(WagerGroupNames, `winGroup.abbr.${index}`, "");
          } else {
            label = !isLeg
              ? formatSequencial(index + 1)
              : `R${raceNumber + index}`;
          }
        }

        // $FlowFixMe
        return (
          (!isSpecialGroup || (isSpecialGroup && !!selection.length)) && (
            <RunnerNumberGroup
              key={`runner-group${label}`}
              isActive={index === betLeg}
              runners={selection}
              horseNamesList={horseNamesList}
              raceTypeCode={raceTypeCode}
              isKey={key}
              isLeg={isLeg}
              label={label}
              favoriteRunners={normalizedFavs}
              isVertical={isVertical}
              isSpecialGroup={isSpecialGroup}
              hasScrollOverflow={hasScrollOverflow}
              useNewProgramPageLayout={useNewProgramPageLayout}
            />
          )
        );
      })}
    </Fragment>
  );
};

class BetSummary extends Component<Props> {
  static defaultProps = {
    onDelete: noop,
    isCancelable: false,
    betTotal: 0,
    wagerAmount: 0,
    selectionType: "",
    selections: [],
    horseNamesList: [],
    favoriteRunners: [],
    raceTypeCode: "T",
    isKey: false,
    isLeg: false,
    isVertical: true,
    isLost: false,
    isCancelled: false,
    isRefunded: false,
    totalWinAmount: null,
    className: "",
    raceNumber: "1",
    layout: "full",
    showAllSelections: true,
    borderTotalWidth: false,
    isBorderBottom: false,
    probables: [],
    wagerType: { id: 0, code: "WN", name: "" },
    showPotentialReturn: false,
    openPotencialReturnModal: noop,
    isProcessing: false,
    isBetPlaced: false,
    isBettingError: false,
    showTag: false,
    remainingDayCancelledCount: 0,
    status: { code: "O" }
  };

  shouldComponentUpdate(nextProps: Props) {
    return (
      this.props.selections.length !== nextProps.selections.length ||
      !isEqual(this.props, nextProps)
    );
  }

  renderBetDetails() {
    const totalBets = getNumberOfWagers(
      get(this.props, "betTotal", 1),
      get(this.props, "wagerAmount", 1)
    );

    return (
      <BetDetails>
        <BetType>
          <BetInfoTitle data-qa-label="betSummary-betAmount-amount">
            {this.props.selectionType}
          </BetInfoTitle>
          <BetInfoSubTitle data-qa-label="betSummary-betAmount-betType">
            Bet Type
          </BetInfoSubTitle>
        </BetType>
        <TicketAmount>
          <BetInfoTitle data-qa-label="betSummary-betAmount-betTotal">
            {formatCurrency(this.props.betTotal || 0)}
          </BetInfoTitle>
          <BetInfoSubTitle>
            {totalBets > 1
              ? `${formatCurrency(
                  this.props.wagerAmount || 0
                )} wager (x${totalBets})`
              : `Total Wager`}
          </BetInfoSubTitle>
        </TicketAmount>
      </BetDetails>
    );
  }

  renderProbables = (probable: ?string) => {
    const hasProbables = doesRaceWagerTypeHasProbables(
      this.props.probables,
      get(this.props, "wagerType.id")
    );
    return this.props.showPotentialReturn && probable !== "0" ? (
      <ProbablesAmount
        onClick={() => this.props.openPotencialReturnModal(!hasProbables)}
      >
        <BetInfoTitle data-qa-label="betSummary-betAmount-amount">
          {hasProbables ? probable : "N/A"}
        </BetInfoTitle>
        <BetInfoSubTitle data-qa-label="betSummary-betAmount-betType">
          {this.props.showPotentialReturn && (
            <IconExclamationContainer>
              <Icon
                icon={info}
                size={12}
                qaLabel="WarningIcon"
                color={buildColor("blue", "500")}
              />
              {!hasProbables && <RedCircle />}
            </IconExclamationContainer>
          )}
          Pot. Return
        </BetInfoSubTitle>
      </ProbablesAmount>
    ) : (
      this.props.isCancelable && this.renderTrashButton()
    );
  };

  renderTrashButton() {
    return (
      <Button
        type="simpleLink"
        onClick={this.props.onDelete}
        qaLabel="betSummary-cancelBetBtn"
        size="noSize"
        paddingLess
      >
        <Icon size={17} icon={trash} color={buildColor("red", "500")} />
      </Button>
    );
  }

  renderSelections() {
    const simpleWagerCodesArray = ["PL", "WN", "SH", "WP", "WS", "PS", "WPS"];
    const horseNamesList = simpleWagerCodesArray.includes(
      this.props.wagerType.code
    )
      ? this.props.horseNamesList
      : [];

    return getListRunnerNumberGroup(
      horseNamesList,
      this.props.selections,
      this.props.raceTypeCode,
      this.props.isKey,
      this.props.isLeg,
      +this.props.raceNumber,
      this.props.favoriteRunners,
      0,
      this.props.showAllSelections,
      this.props.isVertical
    );
  }

  renderStatus() {
    const { isProcessing, isBettingError, isBetPlaced } = this.props;
    let render;

    if (isProcessing) {
      render = (
        <Loading spanSize={8}>
          <span />
          <span />
          <span />
        </Loading>
      );
    } else if (isBetPlaced) {
      render = <Tag name="placed" tagType="easyTag" useNewLayout />;
    } else if (isBettingError) {
      render = <Tag name="failed" tagType="veryhardTag" useNewLayout />;
    }

    return render && <TagContainer>{render}</TagContainer>;
  }

  renderFullBetSummary() {
    const probable = getCurrentProbableValue(
      this.props.probables,
      this.props.wagerType,
      this.props.wagerAmount,
      this.props.selections
    );

    return (
      <BetSummaryContainer
        data-qa-label="betSummary"
        className={this.props.className}
        borderWidth={this.props.borderTotalWidth}
        isBorderBottom={this.props.isBorderBottom}
      >
        <BetDetailsContainer
          isCancelable={
            this.props.isCancelable &&
            !(this.props.showPotentialReturn && probable !== "0")
          }
        >
          {this.renderBetDetails()}
          {this.renderProbables(probable)}
          {this.props.isCancelled && (
            <BetLabel
              bgColor={buildColor("grey", "100")}
              color={buildColor("grey", "900")}
              data-qa-label="betSummary-cancelledLabel"
            >
              Cancelled
            </BetLabel>
          )}
          {this.props.isRefunded && (
            <BetLabel
              bgColor={buildColor("grey", "100")}
              color={buildColor("grey", "900")}
              data-qa-label="betSummary-refundedLabel"
            >
              Refunded
            </BetLabel>
          )}
          {this.props.isLost && (
            <BetLabelWrapper>
              <BetValue
                color={buildColor("grey", "900")}
                data-qa-label="betSummary-lostValue"
              >
                $0.00
              </BetValue>
              <BetLabel
                color={buildColor("grey", "900")}
                data-qa-label="betSummary-lostLabel"
              >
                Return
              </BetLabel>
            </BetLabelWrapper>
          )}

          {this.props.totalWinAmount && (
            <BetLabelWrapper>
              <BetValue
                color={buildColor("green")}
                data-qa-label="betSummary-winValue"
                bold
              >
                {formatCurrency(this.props.totalWinAmount)}
              </BetValue>
              <BetLabel
                color={buildColor("grey", "800")}
                data-qa-label="betSummary-winlabel"
              >
                Won
              </BetLabel>
            </BetLabelWrapper>
          )}
          {this.props.showTag && this.renderStatus()}
        </BetDetailsContainer>
        <SelectionsContainer
          data-qa-label="runnerSelections"
          isVertical={this.props.isVertical}
          layout={this.props.layout}
          showPotentialReturn={this.props.showPotentialReturn}
        >
          {this.renderSelections()}
          {this.props.showPotentialReturn &&
            this.props.status?.code !== "RO" &&
            this.props.status?.code !== "SK" &&
            probable !== "0" && (
              <TrashContainer>{this.renderTrashButton()}</TrashContainer>
            )}
        </SelectionsContainer>
      </BetSummaryContainer>
    );
  }

  renderSimpleBetSummary() {
    return (
      <div className={`simpleBetSummaryWrapper ${this.props.className}`}>
        {this.renderBetDetails()}
        <SelectionsContainer
          data-qa-label="runnerSelections"
          isVertical={this.props.isVertical}
          layout={this.props.layout}
        >
          {this.renderSelections()}
        </SelectionsContainer>
      </div>
    );
  }

  render() {
    switch (this.props.layout) {
      case "simple":
        return this.renderSimpleBetSummary();

      case "full":
      default:
        return this.renderFullBetSummary();
    }
  }
}

export default BetSummary;
